import type { QueryKey } from '@tanstack/react-query';

import { apiListQuery, apiQuery, createQueryKeys, useApiMutation, type ApiMutationOptions, type ApiMutationResult } from '@/shared/data';
import { getCurrentTenant } from '@/shared/http';

import * as api from './credit-note.api';
import type { AssignBody, CancelBody, DisapproveBody, DuePaymentBody, ListCancelBody, ListDisapproveBody, ManualUpdateBody, ReprocessStep, ReserveTokenCreate, ShareBody } from './credit-note.wire';

// Cache e mutações da Operação de crédito (U35, shared/data). Sem texto de UI: o toast de sucesso de
// cada ação (literal do legado no SPEC) chega pela tela em `options.success`.

export const creditNoteKeys = createQueryKeys('credit-note');

/** Chamada com `withTenant` leva o tenant na chave, para o cache não vazar entre correspondentes. */
const withTenantKey = <P extends object>(params: P) => ({ ...params, tenant: getCurrentTenant() ?? null });

export const creditNoteListQuery = (params: api.CreditNoteListParams) =>
  apiListQuery(creditNoteKeys.list(withTenantKey(params)), ({ signal }) => api.listCreditNotes(params, signal));

/** O legado não busca a operação nova (`id === 'nova'`, creditContext.tsx:84). */
export const creditNoteDetailQuery = (id: string) =>
  apiQuery(creditNoteKeys.detail(id), ({ signal }) => api.getCreditNote(id, signal), { enabled: id !== 'nova' && id !== '' });

export const creditNoteProductQuery = (productId: string) =>
  apiQuery(creditNoteKeys.custom('product', productId, withTenantKey({})), ({ signal }) => api.getCreditNoteProduct(productId, signal), {
    enabled: productId !== '' && productId !== 'novo',
  });

export const rejectionReasonsQuery = () => apiQuery(creditNoteKeys.custom('rejection-reasons'), ({ signal }) => api.listRejectionReasons(signal));

/** Só root consulta (complianceCheckListContext.tsx:16); quem chama passa `enabled`. */
export const personComplianceRestrictionsQuery = (personId: string, enabled = true) =>
  apiQuery(creditNoteKeys.custom('compliance-restrictions', personId), ({ signal }) => api.listPersonComplianceRestrictions(personId, signal), {
    enabled: enabled && personId !== '',
  });

export const creditNoteWorkflowsQuery = (id: string) => apiQuery(creditNoteKeys.sub(id, 'workflows'), ({ signal }) => api.listCreditNoteWorkflows(id, signal));

export const creditNoteCommentsQuery = (id: string, page: number) =>
  apiListQuery(creditNoteKeys.sub(id, 'comments', withTenantKey({ page })), ({ signal }) => api.listCreditNoteComments(id, page, signal));

/**
 * O prefixo do `GET /Signature/{id}` (de `shared/signature`) no cache da operação: a lista, o "Ver detalhes" e
 * as ações leem a mesma chave, e o "Atualizar" do detalhe (`creditNoteKeys.all`) recarrega o status.
 */
export const CREDIT_NOTE_SIGNATURE_SCOPE = creditNoteKeys.custom('signature-workflow');

export const signatureUrlsQuery = (id: string) => apiQuery(creditNoteKeys.sub(id, 'signatures'), ({ signal }) => api.listSignatureUrls(id, signal));

export const cosifTransferAccountsQuery = () => apiQuery(creditNoteKeys.custom('cosif-transfer'), ({ signal }) => api.listCosifTransferAccounts(signal));

export const liquidationScheduleQuery = (id: string) =>
  apiQuery(creditNoteKeys.sub(id, 'liquidation-schedule'), ({ signal }) => api.listLiquidationSchedule(id, signal), { enabled: id !== 'nova' });

export const availableReserveTokensQuery = (personId: string) =>
  apiQuery(creditNoteKeys.custom('reserve-tokens', personId), ({ signal }) => api.listAvailableReserveTokens(personId, signal), { enabled: personId !== '' });

export const datasetKey = (recordType: api.DatasetRecordType, id: string, dataset: string) => creditNoteKeys.custom('dataset', recordType, id, dataset);

/** Leitura do último resultado; `staleTime` de 2 min como o legado (datasetContext.tsx:85). */
export const datasetQuery = (recordType: api.DatasetRecordType, id: string, dataset: string, enabled = true, params?: Readonly<Record<string, string | number | boolean>>) =>
  apiQuery(datasetKey(recordType, id, dataset), ({ signal }) => api.getDataset(recordType, id, dataset, signal, params), {
    staleTime: 120_000,
    enabled: enabled && id !== '' && id !== 'novo',
  });

/** Tokens de averbação da pessoa (`useGetTokenRegisteredByPersonId`, naturalPersonContext.tsx:171-205). */
export const reserveTokenDatasetQuery = (personId: string, enabled = true) =>
  apiQuery(creditNoteKeys.custom('reserve-token-dataset', personId), ({ signal }) => api.listReserveTokenDataset(personId, signal), { enabled: enabled && personId !== '' });

export const dataprevMarginKey = (params: api.DataprevMarginParams) => creditNoteKeys.custom('dataprev-margin', params.registrationNumber ?? null, params.personId ?? null, params.creditProductId ?? null);

/** Margem do Crédito do Trabalhador guardada (`useDataprevMarginQuery`, contexts/dataprev/index.ts:75-118): só com o acordeão aberto. */
export const dataprevMarginQuery = (params: api.DataprevMarginParams, enabled = true) => ({
  ...apiQuery(dataprevMarginKey(params), ({ signal }) => api.getDataprevMarginQuery(params, signal), {
    enabled: enabled && Boolean(params.personId || params.registrationNumber || params.creditProductId),
  }),
  retry: false,
});

/** Tokens do tomador no formato real (`result.Token`), para os seletores de token da barra. */
export const registeredReserveTokensKey = (personId: string) => creditNoteKeys.custom('registered-reserve-tokens', personId);
export const registeredReserveTokensQuery = (personId: string, enabled = true) =>
  apiQuery(registeredReserveTokensKey(personId), ({ signal }) => api.listRegisteredReserveTokens(personId, signal), { enabled: enabled && personId !== '' });

/** Operações do "Calendário de troco" (`useGetPublicPayrollLoan`); só com o painel aberto. */
export const publicPayrollLoansQuery = (id: string, enabled = true) =>
  apiQuery(creditNoteKeys.sub(id, 'public-payroll-loans'), ({ signal }) => api.listPublicPayrollLoans(id, signal), { enabled: enabled && id !== '' && id !== 'nova' });

// ─── escrita nas abas (trilha "abas") ───────────────────────────────────────

/** Signatários da PJ ("Importar signatários" do drawer "Envolvido"). */
export const legalPersonSignatoriesQuery = (id: string, enabled = true) =>
  apiQuery(creditNoteKeys.custom('legal-person-signatories', id), ({ signal }) => api.getLegalPersonSignatories(id, signal), { enabled: enabled && id !== '' });

// ─── mutações ───────────────────────────────────────────────────────────────

interface MutationDef<V, D> {
  readonly run: (variables: V) => Promise<D>;
  readonly invalidate: (variables: V) => readonly QueryKey[];
}

const onRecord = <V extends { id: string }, D>(run: (variables: V) => Promise<D>): MutationDef<V, D> => ({
  run,
  invalidate: (variables) => [creditNoteKeys.lists(), creditNoteKeys.detail(variables.id)],
});
const onModule = <V, D>(run: (variables: V) => Promise<D>): MutationDef<V, D> => ({ run, invalidate: () => [creditNoteKeys.all] });

type Id = { id: string };


/** Uma entrada por chamada de escrita do fio; a tela usa `useCreditNoteMutation(nome, opções)`. */
export const creditNoteMutations = {
  submitApproval: onRecord(({ id, updateStartDate }: Id & { updateStartDate?: boolean }) => api.submitApproval(id, updateStartDate)),
  updateFormValues: onRecord(({ id, values }: Id & { values: Record<string, unknown> }) => api.updateCreditNoteFormValues(id, values)),
  approveStep: onRecord(({ id, status, body }: Id & { status: api.ApprovableStatus; body?: Parameters<typeof api.approveStep>[2] }) => api.approveStep(id, status, body)),
  approveLiquidationFromList: onRecord(({ id }: Id) => api.approveLiquidationFromList(id)),
  disapprove: onRecord(({ id, body }: Id & { body: DisapproveBody | ListDisapproveBody }) => api.disapproveCreditNote(id, body)),
  cancel: onRecord(({ id, body }: Id & { body: CancelBody | ListCancelBody }) => api.cancelCreditNote(id, body)),
  completeSignature: onRecord(({ id, message }: Id & { message?: string | null }) => api.completeSignature(id, message)),
  reviewSignature: onRecord(({ id, reprove, message }: Id & { reprove?: boolean; message?: string | null }) =>
    api.reviewSignature(id, { ...(reprove === undefined ? {} : { reprove }), ...(message === undefined ? {} : { message }) }),
  ),
  reviewPartnerSignature: onRecord(({ id, reprove, message }: Id & { reprove?: boolean; message?: string | null }) =>
    api.reviewPartnerSignature(id, { ...(reprove === undefined ? {} : { reprove }), ...(message === undefined ? {} : { message }) }),
  ),
  sendSignatureLink: onRecord(({ id }: Id) => api.sendSignatureLink(id)),
  sendDocumentsByEmail: onRecord(({ id, email }: Id & { email: string }) => api.sendDocumentsByEmail(id, email)),
  saveDocuments: onRecord(({ id, uploads }: Id & { uploads: readonly Record<string, unknown>[] }) => api.saveDocuments(id, uploads)),
  deleteDocument: onRecord(({ id, uploadId }: Id & { uploadId: string }) => api.deleteDocument(id, uploadId)),
  delete: onRecord(({ id }: Id) => api.deleteCreditNote(id)),
  restore: onRecord(({ id }: Id) => api.restoreCreditNote(id)),
  removeBlock: onRecord(({ id }: Id) => api.removeBlock(id)),
  generateContractPreview: onRecord(({ id, ...options }: Id & api.PreviewOptions) => api.generateContractPreview(id, options)),
  generateAssignmentPreview: onRecord(({ id, ...options }: Id & api.PreviewOptions) => api.generateAssignmentPreview(id, options)),
  releaseToPayment: onRecord(({ id, values }: Id & { values: Parameters<typeof api.releaseToPayment>[1] }) => api.releaseToPayment(id, values)),
  donePaymentRevision: onRecord(({ id, body }: Id & { body: Parameters<typeof api.donePaymentRevision>[1] }) => api.donePaymentRevision(id, body)),
  manualUpdate: onRecord(({ id, body }: Id & { body: ManualUpdateBody }) => api.manualUpdate(id, body)),
  sendToWebCred: onRecord(({ id }: Id) => api.sendToWebCred(id)),
  generateTransferReceipts: onRecord(({ id, ...params }: Id & Parameters<typeof api.generateTransferReceipts>[1]) => api.generateTransferReceipts(id, params)),
  approveWarranty: onRecord(({ id, tokens }: Id & { tokens: readonly Record<string, unknown>[] }) => api.approveWarranty(id, tokens)),
  disapproveManualWarranty: onRecord(({ id, cancel, message }: Id & { cancel: boolean; message: string }) => api.disapproveManualWarranty(id, { cancel, message })),
  doneWarrantyRevision: onRecord(({ id, values }: Id & { values: Record<string, unknown> }) => api.doneWarrantyRevision(id, values)),
  updateRevisionWarranty: onRecord(({ id, body }: Id & { body: Parameters<typeof api.updateRevisionWarranty>[1] }) => api.updateRevisionWarranty(id, body)),
  approveMarginReserve: onRecord(({ id, body }: Id & { body: Record<string, unknown> }) => api.approveMarginReserve(id, body)),
  disapproveMarginReserve: onRecord(({ id, body }: Id & { body: Parameters<typeof api.disapproveMarginReserve>[1] }) => api.disapproveMarginReserve(id, body)),
  approveVehicleInspection: onRecord(({ id, message }: Id & { message: string }) => api.approveVehicleInspection(id, message)),
  checkAverbation: onRecord(({ id }: Id) => api.checkAverbation(id)),
  updateSiapeStatus: onRecord(({ id }: Id) => api.updateSiapeStatus(id)),
  updateZetraStatus: onRecord(({ id }: Id) => api.updateZetraStatus(id)),
  checkOperationalLimit: onRecord(({ id }: Id) => api.checkOperationalLimit(id)),
  accountOpeningReview: onRecord(({ id, body }: Id & { body: Record<string, unknown> }) => api.accountOpeningReview(id, body)),
  postComment: {
    run: ({ id, comment }: Id & { comment: string }) => api.postComment(id, comment),
    invalidate: ({ id }: Id) => [creditNoteKeys.sub(id, 'comments')],
  } satisfies MutationDef<Id & { comment: string }, unknown>,
  updateLiquidationClosureStatus: onRecord(({ id, body }: Id & { body: Parameters<typeof api.updateLiquidationClosureStatus>[1] }) => api.updateLiquidationClosureStatus(id, body)),
  closeTransitoryAccount: onRecord(({ id, body }: Id & { body: Parameters<typeof api.closeTransitoryAccount>[1] }) => api.closeTransitoryAccount(id, body)),
  updateInsurance: onRecord(({ id, insurance }: Id & { insurance: boolean }) => api.updateInsurance(id, insurance)),
  updateFund: onRecord(({ id, fundId }: Id & { fundId: string }) => api.updateFund(id, fundId)),
  share: onRecord(({ id, body }: Id & { body: Parameters<typeof api.shareCreditNote>[1] }) => api.shareCreditNote(id, body)),
  /** Consulta paga ao bureau (U24: `write` com prévia). Invalida só a leitura do mesmo dataset. */
  requestDataset: {
    run: ({ recordType, id, dataset }: { recordType: api.DatasetRecordType; id: string; dataset: string }) => api.requestDataset(recordType, id, dataset),
    invalidate: ({ recordType, id, dataset }: { recordType: api.DatasetRecordType; id: string; dataset: string }) => [datasetKey(recordType, id, dataset)],
  } satisfies MutationDef<{ recordType: api.DatasetRecordType; id: string; dataset: string }, unknown>,
  /** Consulta nova de margem na Dataprev (paga, só humano com prévia, X01). */
  requestDataprevMargin: {
    run: ({ params }: { params: api.DataprevMarginParams; readKey: api.DataprevMarginParams }) => api.requestDataprevMarginQuery(params),
    invalidate: ({ readKey }: { params: api.DataprevMarginParams; readKey: api.DataprevMarginParams }) => [dataprevMarginKey(readKey)],
  } satisfies MutationDef<{ params: api.DataprevMarginParams; readKey: api.DataprevMarginParams }, unknown>,
  assign: onRecord(({ id, body }: Id & { body: AssignBody }) => api.assignCreditNote(id, body)),
  sharePerson: onModule(({ resource, personId, body }: { resource: 'NaturalPerson' | 'LegalPerson'; personId: string; body: ShareBody }) => api.sharePersonRecord(resource, personId, body)),
  disapproveMarginReserveForm: onRecord(({ id, body }: Id & { body: Parameters<typeof api.disapproveMarginReserveForm>[1] }) => api.disapproveMarginReserveForm(id, body)),
  /** Tokens novos do tomador: os seletores de token (aprovar reserva, aprovar garantia) recarregam. */
  requestReserveTokens: {
    run: ({ personId, tokens }: { personId: string; tokens: readonly ReserveTokenCreate[] }) => api.requestReserveTokens(personId, tokens),
    invalidate: ({ personId }: { personId: string; tokens: readonly ReserveTokenCreate[] }) => [registeredReserveTokensKey(personId), creditNoteKeys.custom('reserve-tokens', personId)],
  } satisfies MutationDef<{ personId: string; tokens: readonly ReserveTokenCreate[] }, unknown>,
  checkSemearProposal: onRecord(({ id }: Id) => api.checkSemearProposalStatus(id)),
  /** Cálculo do calendário de quitação: não muda a operação. */
  calculateDuePayment: {
    run: ({ id, body }: Id & { body: DuePaymentBody }) => api.calculateDuePayment(id, body),
    invalidate: () => [],
  } satisfies MutationDef<Id & { body: DuePaymentBody }, unknown>,
  sendToWalletManagement: onModule(({ ids }: { ids: readonly string[] }) => api.sendToWalletManagement(ids)),
  reprocessByStatus: onModule(({ step, ids }: { step: ReprocessStep; ids: readonly string[] }) => api.reprocessByStatus(step, ids)),
  linkEmployer: onModule((body: Parameters<typeof api.linkEmployer>[0]) => api.linkEmployer(body)),
  importVehicleWarranty: onModule(({ file }: { file: File }) => api.importVehicleWarranty(file)),
  startBatchAssignment: onModule(({ ids, values }: { ids: readonly string[]; values?: Record<string, unknown> }) => api.startBatchAssignment(ids, values)),
  deleteComplianceRestrictions: onModule(({ ids }: { ids: readonly string[] }) => api.deleteComplianceRestrictions(ids)),
} as const;

export type CreditNoteMutationName = keyof typeof creditNoteMutations;
type Def<K extends CreditNoteMutationName> = (typeof creditNoteMutations)[K];
export type CreditNoteMutationVariables<K extends CreditNoteMutationName> = Parameters<Def<K>['run']>[0];
export type CreditNoteMutationData<K extends CreditNoteMutationName> = Awaited<ReturnType<Def<K>['run']>>;

/**
 * Mutação de uma ação do fio com o feedback padrão (`useApiMutation`). As chaves do registro e da
 * lista são invalidadas depois do sucesso; `options.invalidate` substitui.
 */
export function useCreditNoteMutation<K extends CreditNoteMutationName>(
  name: K,
  options: Omit<ApiMutationOptions<CreditNoteMutationData<K>, CreditNoteMutationVariables<K>>, 'mutationFn'> = {},
): ApiMutationResult<CreditNoteMutationData<K>, CreditNoteMutationVariables<K>> {
  const def = creditNoteMutations[name] as unknown as MutationDef<CreditNoteMutationVariables<K>, CreditNoteMutationData<K>>;
  return useApiMutation<CreditNoteMutationData<K>, CreditNoteMutationVariables<K>>({
    mutationKey: ['credit-note', 'mutation', name],
    invalidate: (_data, variables) => def.invalidate(variables),
    ...options,
    mutationFn: def.run,
  });
}
